import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  ArrowLeft, ExternalLink, Download, Building2, Users, Leaf,
  Calendar, MapPin, FileText, CheckCircle, AlertCircle
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function ProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPapers, setRelatedPapers] = useState([]);

  useEffect(() => {
    fetchSchemeDetail();
    fetchRelatedPapers();
  }, [id]);

  const fetchRelatedPapers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/${id}/related-papers`);
      setRelatedPapers(data.related_papers || []);
    } catch (error) {
      console.error('Error fetching related papers:', error);
    }
  };

  const fetchSchemeDetail = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/${id}`);
      setScheme(data);
    } catch (error) {
      console.error('Error fetching scheme:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPillarIcon = (pillarName) => {
    if (pillarName.includes('Economic')) return '💼';
    if (pillarName.includes('Social')) return '🤝';
    if (pillarName.includes('Environmental')) return '🌱';
    return '📋';
  };

  const getPillarColor = (pillarName) => {
    if (pillarName.includes('Economic')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (pillarName.includes('Social')) return 'bg-green-100 text-green-800 border-green-200';
    if (pillarName.includes('Environmental')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading scheme details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!scheme) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Scheme Not Found</h2>
            <p className="text-gray-600 mb-4">The requested scheme could not be found.</p>
            <Button onClick={() => navigate('/programs')}>Back to Programs</Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate('/programs')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Programs
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Card className="p-8 mb-6">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge className={getPillarColor(scheme.primary_pillar)}>
                      {getPillarIcon(scheme.primary_pillar)} {scheme.primary_pillar}
                    </Badge>
                    {scheme.status === 'active' && (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    )}
                    {scheme.status === 'pilot' && (
                      <Badge className="bg-yellow-100 text-yellow-800">Pilot</Badge>
                    )}
                  </div>
                  
                  <h1 className="text-3xl font-bold text-[#1E3A5F] mb-4">
                    {scheme.scheme_name}
                  </h1>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {scheme.ministry_department}
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {scheme.government_level} Government
                    </div>
                    {scheme.launch_year && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Launched {scheme.launch_year}
                      </div>
                    )}
                  </div>
                </div>

                {/* Objective */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-[#1E3A5F] mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Objective
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {scheme.objective_summary}
                  </p>
                </div>

                {/* Key Components */}
                {scheme.key_components && scheme.key_components.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">Key Components</h2>
                    <ul className="space-y-2">
                      {scheme.key_components.map((component, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{component}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Eligibility */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">Eligibility Criteria</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {scheme.eligibility_criteria}
                  </p>
                </div>

                {/* Financial Benefits */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">Financial Benefits</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {scheme.financial_benefits_allocation}
                  </p>
                </div>

                {/* Implementation */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">Implementation Mechanism</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {scheme.implementation_mechanism}
                  </p>
                </div>

                {/* Application Process */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">How to Apply</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {scheme.application_process}
                  </p>
                </div>

                {/* Timeline */}
                {scheme.timeline_deadlines && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-[#1E3A5F] mb-3">Timeline & Deadlines</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {scheme.timeline_deadlines}
                    </p>
                  </div>
                )}
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Use Case */}
              <Card className="p-6 mb-6 sticky top-4">
                <h3 className="font-bold text-lg mb-4">Applicable For</h3>
                <div className="space-y-3">
                  {scheme.applies_to_msmes && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">MSMEs</span>
                    </div>
                  )}
                  {scheme.applies_to_ngos && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <Users className="h-5 w-5 text-green-600" />
                      <span className="font-medium">NGOs</span>
                    </div>
                  )}
                  {scheme.applies_to_csr_programs && (
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                      <Leaf className="h-5 w-5 text-emerald-600" />
                      <span className="font-medium">CSR Programs</span>
                    </div>
                  )}
                </div>

                {/* Official Links */}
                {scheme.official_website && (
                  <div className="mt-6">
                    <Button 
                      className="w-full" 
                      onClick={() => window.open(scheme.official_website, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visit Official Website
                    </Button>
                  </div>
                )}

                {/* Documents */}
                {scheme.official_documents && scheme.official_documents.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-bold mb-3">Official Documents</h3>
                    <div className="space-y-2">
                      {scheme.official_documents.map((doc, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {doc.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Classification */}
              <Card className="p-6">
                <h3 className="font-bold text-lg mb-4">Classification</h3>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Themes</p>
                  <div className="space-y-1">
                    {scheme.themes.map((theme, idx) => (
                      <div key={idx} className="text-sm bg-gray-100 px-3 py-1.5 rounded">
                        {theme}
                      </div>
                    ))}
                  </div>
                </div>

                {scheme.keywords && scheme.keywords.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {scheme.keywords.map((keyword, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Related Research Papers */}
              {relatedPapers.length > 0 && (
                <Card className="p-6 mt-6" data-testid="related-papers-card">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#E87722]" />
                    Related Research
                  </h3>
                  <div className="space-y-3">
                    {relatedPapers.map((paper) => (
                      <button
                        key={paper.id}
                        onClick={() => navigate(`/research-library/paper/${paper.id}`)}
                        className="w-full text-left p-3 border rounded-lg hover:border-[#E87722] hover:bg-orange-50/50 transition-colors"
                        data-testid={`related-paper-${paper.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm text-[#1E3A5F] line-clamp-2">{paper.title}</p>
                          <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${paper.link_type === 'manual' ? 'border-[#E87722] text-[#E87722]' : ''}`}>
                            {paper.link_type === 'manual' ? 'Curated' : 'Suggested'}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{paper.theme}</p>
                      </button>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
